import { useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadingSelector } from '~/GlobalRedux/Features/loading/loadingSlice';
import {
  initialFormValue,
  listaRelacaoEN,
  listaRelacaoES,
  listaRelacaoPT,
} from './texts';
import { FormValues } from './tickets/types';
import { setLanguage } from '~/GlobalRedux/Features/language/languageSlice';
import { Languages } from '~/components/LanguageSelector';
import { handleSubmitForm } from './submitForm';

const useFormTools = (language: Languages) => {
  const dispatch = useDispatch();
  const loading = useSelector(loadingSelector);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const [formValues, setFormValues] = useState<FormValues>(initialFormValue);
  const [warning, setWarning] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      handleForm('captchaToken', token);
      setIsModalOpen(false);
    }
  };

  useLayoutEffect(() => {
    dispatch(setLanguage('pt'));
  }, [dispatch]);

  const indicesToSanitize: Array<keyof FormValues> = [
    'fullName',
    'np',
    'supplier',
  ];

  const sanitizeInput = (input: string) => {
    return input.replace(/[^\w\s]/gi, '');
  };

  const handleForm = (index: keyof FormValues, value: string | boolean) => {
    let sanitizedValue = value;

    if (typeof value === 'string' && indicesToSanitize.includes(index)) {
      sanitizedValue = sanitizeInput(value);
    }
    setFormValues(prevValues => ({
      ...prevValues,
      [index]: sanitizedValue,
    }));
    if (isButtonEnabled) setWarning(false);
  };

  const handleBrandsAndSegments = (
    type: 'brands' | 'segments',
    value: string,
  ) => {
    setFormValues(prevValues => ({
      ...prevValues,
      [type]: prevValues[type].includes(value)
        ? prevValues[type].filter(prev => prev !== value)
        : [...prevValues[type], value],
    }));
  };

  const lists = {
    pt: listaRelacaoPT,
    en: listaRelacaoEN,
    es: listaRelacaoES,
  };

  const listToUse = lists[language];

  const isEmployeeRelated = [
    listToUse[0].name,
    listToUse[1].name,
    listToUse[2].name,
  ].includes(formValues.relation);

  const isSupplier = formValues.relation === listToUse[7].name;

  const isNeededBrandAndSegments = [
    listToUse[4].name,
    listToUse[5].name,
    listToUse[6].name,
  ].includes(formValues.relation);

  function validateForm(
    formValues: FormValues,
    isEmployeeRelated: boolean,
    isSupplier: boolean,
    isNeededBrandAndSegments: boolean,
  ): boolean {
    const {
      request,
      requestDetail,
      captchaToken,
      iAm,
      relation,
      fullName,
      eMail,
      photo,
      np,
      suzano_mail,
      supplier,
      corp_mail,
      brands,
      segments,
      authorize,
    } = formValues;

    if (
      !request ||
      !requestDetail ||
      !captchaToken ||
      !iAm ||
      !relation ||
      !fullName ||
      !eMail ||
      !emailRegex.test(eMail) ||
      !photo ||
      !authorize
    ) {
      return false;
    }

    if (isEmployeeRelated && (!np || !suzano_mail)) {
      return false;
    }

    if (isSupplier && (!supplier || !corp_mail)) {
      return false;
    }

    if (isNeededBrandAndSegments && (!brands.length || !segments.length)) {
      return false;
    }

    return true;
  }

  const isButtonEnabled = validateForm(
    formValues,
    isEmployeeRelated,
    isSupplier,
    isNeededBrandAndSegments,
  );

  const submitForm = () => {
    if (!formValues.captchaToken) {
      setIsModalOpen(true);
      return;
    }
    handleSubmitForm(
      formValues,
      isButtonEnabled,
      setWarning,
      dispatch,
      setFormValues,
      language,
    );
  };

  const customStyles = {
    content: {
      width: '400px',
      height: '250px',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  };

  return {
    customStyles,
    submitForm,
    handleBrandsAndSegments,
    loading,
    warning,
    isModalOpen,
    handleCaptchaChange,
    formValues,
    handleForm,
    isEmployeeRelated,
    isSupplier,
    isNeededBrandAndSegments,
    emailRegex,
    isButtonEnabled,
    setIsModalOpen,
  };
};

export default useFormTools;
